import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { toast } from '@verlo/shared/components/ui/sonner';
import { TONE_NAMES } from '@verlo/shared/lib/tones';
import { useState } from 'react';
import { PageHeader, Stat, Stats, DataTable, FilterBar, Empty, Loading } from '../../components/office';

// The living reference for the design language: every token and every house piece, in
// every state, on one page. Build against this, not against a page that happens to look
// right. Reachable at /back-office/design; not in the nav.

const PALETTE = [
  { name: 'verlo', hex: '#201B5F', cls: 'bg-verlo', means: 'Here · primary action' },
  { name: 'verlo-hover', hex: '#3A31A3', cls: 'bg-verlo-hover', means: 'Hover · links' },
  { name: 'verlo-tint', hex: '#ECEAF9', cls: 'bg-verlo-tint', means: 'Selected · hover ground' },
  { name: 'paper', hex: '#F5F4F0', cls: 'bg-paper', means: 'The ground' },
  { name: 'surface', hex: '#FFFFFF', cls: 'bg-surface', means: 'Cards' },
  { name: 'hairline', hex: '#E4E1DA', cls: 'bg-hairline', means: 'Every border' },
  { name: 'ink', hex: '#1B1A2E', cls: 'bg-ink', means: 'Text' },
  { name: 'ink-soft', hex: '#55536E', cls: 'bg-ink-soft', means: 'Secondary text' },
  { name: 'ink-faint', hex: '#6F6D86', cls: 'bg-ink-faint', means: 'Labels · placeholders' },
];

const SIGNALS = [
  { tone: 'warn', name: 'copper', ink: '#8F4A17', line: '#D07A3E', tint: '#FBEBE0', means: 'Time slipping, needs a look' },
  { tone: 'stop', name: 'claret', ink: '#7E1E3A', line: '#C4536F', tint: '#F9E6EA', means: 'Money, food or safety waiting · errors · allergies' },
  { tone: 'ok', name: 'moss', ink: '#1F5F45', line: '#5FA37F', tint: '#E2F1E8', means: 'Done, seated, paid' },
  { tone: 'info', name: 'navy', ink: '#3A31A3', line: '#3A31A3', tint: '#ECEAF9', means: 'Neutral · sent · confirmed' },
];

const Section = ({ title, rule, children }) => (
  <section className="border-t border-hairline pt-6">
    <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <h2 className="font-heading text-lg font-extrabold text-ink">{title}</h2>
      {rule && <p className="text-[13px] text-ink-soft">{rule}</p>}
    </div>
    {children}
  </section>
);

const Label = ({ children }) => (
  <div className="mb-2 font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">{children}</div>
);

const DesignPage = () => {
  const [seg, setSeg] = useState('floor');
  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6" data-testid="design-page">
      <header>
        <div className="font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-verlo-hover">Verlo · Design</div>
        <h1 className="font-heading text-2xl font-extrabold text-ink">The design language</h1>
        <p className="mt-2 max-w-2xl text-[14px] text-ink-soft">
          Navy is the only strong colour. Signals are ink, not paint. One warm neutral, hairline borders,
          shadows only for things that float. Type does the hierarchy. Every piece below is the real
          component. Build against this page.
        </p>
      </header>

      <Section title="Palette" rule="verlo means here or primary. Nothing else is saturated.">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9">
          {PALETTE.map((c) => (
            <div key={c.name} className="border border-hairline bg-white">
              <div className={`h-12 ${c.cls}`} />
              <div className="p-2">
                <div className="font-heading text-[11px] font-bold uppercase tracking-[0.04em] text-ink">{c.name}</div>
                <div className="font-mono text-[11px] text-ink-soft">{c.hex}</div>
                <div className="mt-1 text-[11px] leading-tight text-ink-faint">{c.means}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Signals" rule="A coloured word and a 3px rail on white, never a pastel box. The tint is for chips and ticket headers only.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SIGNALS.map((s) => (
            <div key={s.tone} className="border border-hairline bg-white">
              <div className="flex h-10">
                <div className="flex-1" style={{ background: s.ink }} />
                <div className="flex-1" style={{ background: s.line }} />
                <div className="flex-1" style={{ background: s.tint }} />
              </div>
              <div className="p-2.5">
                <div className="flex items-baseline justify-between">
                  <span className="font-heading text-[11px] font-bold uppercase tracking-[0.04em] text-ink">signal-{s.tone}</span>
                  <span className="text-[11px] text-ink-faint">{s.name}</span>
                </div>
                <div className="font-mono text-[11px] text-ink-soft">{s.ink} · {s.line} · {s.tint}</div>
                <div className="mt-1 text-[11px] leading-tight text-ink-faint">{s.means}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Type" rule="Manrope names things and carries every figure, tabular. Inter is for reading. There is no code font.">
        <div className="grid gap-4 border border-hairline bg-white p-5 sm:grid-cols-3">
          <div>
            <Label>Heading · Manrope</Label>
            <div className="font-heading text-2xl font-extrabold text-ink">Table 7, Patel</div>
            <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint">Label · uppercase</div>
          </div>
          <div>
            <Label>Body · Inter</Label>
            <p className="text-[14px] text-ink">Mains sent 20:12. The kitchen has the allergy note.</p>
            <p className="text-[13px] text-ink-soft">Secondary text sits in ink-soft, never in a lighter grey.</p>
          </div>
          <div>
            <Label>Figures · Manrope, tabular</Label>
            <div className="font-mono text-2xl text-ink tabular-nums">£1,284.50</div>
            <div className="font-mono text-[13px] text-ink-soft tabular-nums">19:30 · 4 covers · 91m</div>
          </div>
        </div>
      </Section>

      <Section title="Toast" rule="Navy card, no icon, a rail only when it's a signal. Tap anywhere on it to dismiss. Three seconds; errors six. One line, active voice, no exclamation marks.">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => toast('Sent to the kitchen')}>Plain</Button>
          <Button variant="outline" onClick={() => toast.success('Floor plan saved')}>Success</Button>
          <Button variant="outline" onClick={() => toast.error("Couldn't save. T4 was moved by another till.", { action: { label: 'Retry', onClick: () => {} } })}>Error with action</Button>
          <Button variant="outline" onClick={() => toast.warning('Budget not synced. Showing the figure from 2h ago.')}>Warning</Button>
          <Button variant="outline" onClick={() => toast.info('Report sent to 3 people', { description: 'Tuesday, 07:00' })}>Info with description</Button>
        </div>
      </Section>

      <Section title="Notice" rule="Inline, in the flow, where the thing it's about is. The word is the noun (Rota, Allergy), not the tone.">
        <div className="grid gap-2 lg:grid-cols-2">
          <Notice tone="warn" word="Rota" title="4 due on shift, not clocked in" action={<Button size="sm" variant="outline">Open rota</Button>}>
            John Smith, Sarah Jones, Lisa Davis +1
          </Notice>
          <Notice tone="stop" word="Allergy" title="Nut allergy on T2">Shellfish, tree nuts. Told the kitchen at 20:43</Notice>
          <Notice tone="ok" word="Sync" title="Up to date">Last push 20:51</Notice>
          <Notice tone="info" word="Booking" title="Conflict at 19:30">Patel, 4 guests. T7 is taken by Okafor until 20:15</Notice>
          <Notice tone="warn" word="Stock">Short of ribeye: 3 portions left, 6 on order</Notice>
          <Notice tone="info">No word, no title: just the line.</Notice>
        </div>
      </Section>

      <Section title="Signal" rule="The status word. Uppercase Manrope, a dot, the tone's ink on its tint. No icon: the word is the icon.">
        <div className="flex flex-wrap items-center gap-2">
          <Signal tone="warn">late 23 min</Signal>
          <Signal tone="warn">91m over</Signal>
          <Signal tone="stop">allergy</Signal>
          <Signal tone="stop">bill waiting</Signal>
          <Signal tone="ok">seated</Signal>
          <Signal tone="ok">paid</Signal>
          <Signal tone="info">confirmed</Signal>
          <Signal tone="info" dot={false}>walk-in</Signal>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* A table card and a kitchen ticket: the two places a signal lives most. */}
          <div className="border border-hairline border-l-[3px] border-l-signal-warn-line bg-white px-3 py-2.5">
            <div className="flex items-baseline justify-between">
              <span className="font-heading text-[15px] font-extrabold text-ink">T7</span>
              <span className="font-mono text-[12px] text-ink-soft">4 · 19:30</span>
            </div>
            <div className="text-[12.5px] text-ink-soft">Patel, 4 · mains sent 20:12</div>
            <div className="mt-2 flex gap-1.5"><Signal tone="warn">91m over</Signal><Signal tone="stop">allergy</Signal></div>
          </div>
          <div className="border border-hairline bg-white text-[13px]">
            <div className="flex justify-between border-b border-signal-stop-line bg-signal-stop-tint px-2.5 py-2 font-heading font-extrabold text-signal-stop">
              <span>T3 · Mains</span><span className="font-mono text-[12px] font-medium">14:20</span>
            </div>
            <div className="flex gap-2 border-b border-hairline px-2.5 py-1.5"><span className="font-mono text-[12px] text-ink-soft">2</span>Ribeye, medium rare</div>
            <div className="flex gap-2 px-2.5 py-1.5"><span className="font-mono text-[12px] text-ink-soft">1</span>Sea bass · <span className="font-semibold text-signal-stop">no nuts</span></div>
          </div>
        </div>
      </Section>

      <Section title="Controls" rule="From the control sweep: a Segmented control changes what's shown; a button does something; navy means here.">
        <div className="flex flex-wrap items-center gap-3">
          <Segmented value={seg} onChange={setSeg} options={[{ value: 'list', label: 'List' }, { value: 'floor', label: 'Floor plan' }]} />
          <Button>Send to kitchen</Button>
          <Button variant="outline">Hold</Button>
          <Button variant="ghost">Later</Button>
          <Button variant="destructive">Void</Button>
          <Input className="w-48" placeholder="Search tables" />
        </div>
      </Section>

      <Section title="The back office pieces" rule="Corners here: 10px cards, 6px controls. Every page opens with a PageHeader; every number is a Stat; every list is a DataTable.">
        <div className="space-y-6">
          <div className="rounded-lg border border-hairline bg-white p-5">
            <PageHeader title="Stock levels" purpose="What is below par, and what to order today." actions={<><Button variant="outline">Export</Button><Button>Add item</Button></>} className="mb-0" />
          </div>
          <Stats>
            <Stat label="Net sales" value="£1,587" delta={{ text: '+12% vs last Tue', tone: 'ok' }} />
            <Stat label="Covers" value="91" hint="Avg £17.44 a head" />
            <Stat label="Labour" value="28.4%" delta={{ text: '1.2pt over target', tone: 'warn' }} hint="Target 27%" />
            <Stat label="Waste" value="£42" delta={{ text: '0.6% of sales', tone: 'stop' }} to="/back-office/waste" />
          </Stats>
          <div>
            <FilterBar right={<span>3 people</span>}>
              <Input placeholder="Search staff…" className="w-56" />
              <Segmented value="all" onChange={() => {}} options={[{ value: 'all', label: 'All' }, { value: 'foh', label: 'Front' }, { value: 'boh', label: 'Kitchen' }]} />
            </FilterBar>
            <DataTable
              columns={[
                { key: 'name', label: 'Name', sortable: true, render: (r) => <span className="font-medium">{r.name}</span> },
                { key: 'role', label: 'Role', muted: true },
                { key: 'rate', label: 'Rate', align: 'right', mono: true, sortable: true, render: (r) => `£${r.rate.toFixed(2)}/hr` },
                { key: 'hours', label: 'This week', align: 'right', mono: true, sortable: true, render: (r) => `${r.hours}h` },
              ]}
              rows={[{ id: 1, name: 'John Smith', role: 'Manager', rate: 18, hours: 42 }, { id: 2, name: 'Sarah Jones', role: 'Server', rate: 12.5, hours: 31 }, { id: 3, name: 'Mike Brown', role: 'Bartender', rate: 13, hours: 28 }]}
              onRowClick={() => {}}
              actions={() => <Button size="sm" variant="ghost">Edit</Button>}
              defaultSort={{ key: 'name', dir: 'asc' }}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Empty word="Deliveries" title="Nothing booked in yet" action={<Button size="sm">Book a delivery</Button>}>What arrives gets checked against what was ordered, here.</Empty>
            <Loading shape="tiles" count={2} />
          </div>
          <p className="text-[13px] text-ink-soft">Jump: press <kbd className="rounded-md border border-hairline bg-surface-2 px-1.5 py-0.5 font-mono text-[11px]">⌘K</kbd> anywhere in the back office.</p>
        </div>
      </Section>

      <Section title="Tones in code" rule="Every piece takes a tone prop; `@verlo/shared/lib/tones` has the classes for hand-built cards.">
        <pre className="overflow-x-auto border border-hairline bg-white p-4 font-mono text-[12px] text-ink">{`import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { tone } from '@verlo/shared/lib/tones';

<Notice tone="warn" word="Rota" title="4 due on shift, not clocked in">…</Notice>
<Signal tone="stop">allergy</Signal>
<div className={\`border-l-[3px] \${tone('warn').rail}\`}>…</div>   // tones: ${TONE_NAMES.join(' · ')}`}</pre>
      </Section>
    </div>
  );
};

export default DesignPage;
